/**
 * Maps the FE `FilterState` (produced by `deserializeFromUrl`) to the V3
 * `HotpadsWebListingFilter` request shape for `POST /v3/search/listings`.
 *
 * This is the V3 counterpart of `toApiParams` (which targets the flat V2
 * `byCoordsV2` query params). It serves the V3 request paths — SRP, HDP nearby
 * pins, and HDP recommendations; `toApiParams` stays for the remaining V2
 * consumers (dot map, saved searches). Slug-implied filters are NOT expanded
 * here — the SRP sends `searchSlug` and the BE applies the slug defaults, so
 * this maps only the user's explicit URL filters.
 *
 * Property and rental types ride through `mustHave` as SearchFlags (the V3
 * contract has no dedicated `propertyTypes`/`listingTypes` fields); the BE
 * turns the type flags into the index exclusion query.
 */
import type { PartialDeep } from 'type-fest';
import type {
  HpBoundingBoxDto,
  HpSearchFlag,
  HpSearchOrderBy,
  HpWebListingFilterDto,
} from '@zg-rentals/ts-hotpads-api-client';
import type {
  AdditionalOptType,
  AmenityType,
  CreatedWithinType,
  FilterState,
  LaundryType,
  PetType,
  PropertyType,
  RentalType,
  RestrictionType,
} from './filterTypes';
import { normalizeMoveInDate } from './filterValidation';
import { commuteToV3 } from './mappings/commuteMappings';

// Generated request DTOs mark every field required + non-null (Datarouter doesn't
// model optionality); the BE accepts a partial filter, so we send a deep-partial.
type AreaClipId = string | number;
type HotpadsWebListingFilter = PartialDeep<Omit<HpWebListingFilterDto, 'areaId'>> & {
  areaId?: AreaClipId;
  moveInDate?: string;
};
type SearchBoundingBox = HpBoundingBoxDto;
// The FE filter maps work in the BE's persistentString values; `${HpSearchFlag}`
// is the string-literal union of the enum so those maps stay literal, and we
// cast to the nominal enum at the filter boundary (values are identical at runtime).
type SearchFlag = `${HpSearchFlag}`;
type SearchOrderBy = `${HpSearchOrderBy}`;

// FilterState property type → V3 SearchFlag. The index has no large/garden/medium
// distinction, so the FE's collapsed "apartment" maps straight to the apartment flag.
const PROPERTY_TYPE_TO_FLAG: Record<PropertyType, SearchFlag> = {
  apartment: 'apartment',
  condo: 'condo',
  duplex: 'duplex',
  house: 'house',
  townhouse: 'townhouse',
};

// FilterState rental type → V3 listing-type SearchFlag.
const RENTAL_TYPE_TO_FLAG: Record<RentalType, SearchFlag> = {
  corporate: 'corporateHousing',
  rental: 'standardRental',
  room: 'roomForRent',
  sublet: 'sublet',
};

/**
 * Categories whose selected values are a union — a listing matches if it is ANY
 * of them (type flags below become an index exclusion query; two laundry values
 * collapse to the broad `laundry` flag). Dropping one value therefore NARROWS
 * the search, so the only widening move is clearing the whole category. Every
 * other multi-select is a plain `mustHave` AND.
 */
export const UNION_FILTER_KEYS: ReadonlySet<keyof FilterState> = new Set([
  'propertyTypes',
  'rentalTypes',
  'laundry',
]);

const PET_TO_FLAG: Record<PetType, SearchFlag> = { cats: 'allowsCats', dogs: 'allowsDogs' };

const LAUNDRY_TO_FLAG: Record<LaundryType, SearchFlag> = {
  inUnit: 'laundryInUnit',
  shared: 'laundryShared',
};

const AMENITY_TO_FLAG: Record<AmenityType, SearchFlag> = {
  cooling: 'airConditioning',
  heating: 'heating',
  parking: 'parking',
  gatedEntry: 'gatedEntry',
  doorman: 'doorman',
  fitnessCenter: 'fitnessCenter',
  swimmingPool: 'pool',
  dishwasher: 'dishwasher',
};

// 'none' intentionally has no flag.
const RESTRICTION_TO_FLAG: Partial<Record<RestrictionType, SearchFlag>> = {
  income: 'incomeRestricted',
  senior: 'seniorHousing',
  student: 'studentHousing',
  military: 'militaryHousing',
};

const ADDITIONAL_OPT_TO_FLAG: Partial<Record<AdditionalOptType, SearchFlag>> = {
  forRentByOwner: 'listedByOwner',
  acceptingApplications: 'acceptingApplications',
  requiresPhotos: 'photos',
  requiresPrice: 'price',
  hasOffers: 'specialOffers',
};

// FE orderBy values are SearchOrderBy.listingFilterValue; map to the V3 persistentString.
const ORDER_BY_TO_V3: Record<string, SearchOrderBy> = {
  lowPrice: 'priceAsc',
  highPrice: 'priceDesc',
  activated: 'timeActivatedDesc',
  weekViews: 'viewsDesc',
  score: 'scoreDesc',
};

const HOUR_MS = 60 * 60 * 1000;
const CREATED_WITHIN_TO_MS: Record<CreatedWithinType, number> = {
  hour: HOUR_MS,
  day: 24 * HOUR_MS,
  week: 168 * HOUR_MS,
  month: 720 * HOUR_MS,
};

function range(min: number | null, max: number | null): { min?: number; max?: number } | undefined {
  if (min === null && max === null) return undefined;
  return { ...(min !== null ? { min } : {}), ...(max !== null ? { max } : {}) };
}

/**
 * Resolves the area-clip id for `filters.areaId` (V2's "Area Border"). Returns
 * the id only when the border is on AND the id parses to a finite number;
 * otherwise `undefined` (no clip). University areas always return `undefined`:
 * the listing index can't resolve their ids, so a clip returns zero results — we
 * drop it and let the bounding box govern (HPWEB-5750, matching legacy). Keep
 * string ids as strings: HotPads area ids can exceed Number.MAX_SAFE_INTEGER, and
 * converting them corrupts the id. Shared by the SSR request builder and the
 * client live-request hook so both clip identically.
 */
export function toAreaClipId(
  areaId: string | number | null | undefined,
  showBorder: boolean,
  areaType?: string | null,
): AreaClipId | undefined {
  if (!showBorder || areaId == null) return undefined;
  if (areaType === 'university') return undefined;
  const id = typeof areaId === 'string' ? areaId.trim() : areaId;
  if (id === '') return undefined;
  return Number.isFinite(Number(id)) ? id : undefined;
}

export function filterStateToV3Filter(
  filters: FilterState,
  boundingBox?: SearchBoundingBox,
  /**
   * When set, clips results to this area (V3's equivalent of V2's `areas`
   * param / the "Area Border" toggle). Without it the BE constrains only by
   * `boundingBox`, so a viewport wider than the area returns listings from
   * neighbouring areas.
   */
  areaId?: AreaClipId,
): HotpadsWebListingFilter {
  const mustHave: Array<SearchFlag> = [
    ...filters.propertyTypes.map((t) => PROPERTY_TYPE_TO_FLAG[t]),
    ...filters.rentalTypes.map((t) => RENTAL_TYPE_TO_FLAG[t]),
    ...filters.pets.map((p) => PET_TO_FLAG[p]),
    ...(filters.laundry.length === 2 ? (['laundry'] as Array<SearchFlag>) : filters.laundry.map((l) => LAUNDRY_TO_FLAG[l])),
    ...filters.amenities.map((a) => AMENITY_TO_FLAG[a]),
    ...(filters.furnished ? (['furnished'] as Array<SearchFlag>) : []),
    ...(filters.restrictions && RESTRICTION_TO_FLAG[filters.restrictions]
      ? [RESTRICTION_TO_FLAG[filters.restrictions] as SearchFlag]
      : []),
    ...filters.additionalOpts.flatMap((o) =>
      ADDITIONAL_OPT_TO_FLAG[o] ? [ADDITIONAL_OPT_TO_FLAG[o] as SearchFlag] : [],
    ),
  ];

  const filter: HotpadsWebListingFilter = {};

  if (boundingBox) filter.boundingBox = boundingBox;
  if (areaId != null) filter.areaId = areaId;

  const price = range(filters.price.min, filters.price.max);
  if (price) filter.price = price;

  const beds = range(filters.bedrooms.min, filters.bedrooms.max);
  if (beds) filter.beds = beds;

  if (filters.bathrooms.min !== null) filter.baths = { min: filters.bathrooms.min };

  const sqft = range(filters.sqft.min, filters.sqft.max);
  if (sqft) filter.sqft = sqft;

  if (filters.keywords.trim()) filter.keywords = filters.keywords.trim();
  if (filters.orderBy && ORDER_BY_TO_V3[filters.orderBy])
    filter.orderBy = ORDER_BY_TO_V3[filters.orderBy] as HpSearchOrderBy;
  if (filters.createdWithin)
    // The generated MilliDuration type models activatedWithin as `{ millis }`, but the
    // deployed wire contract is a raw millisecond number (Datarouter serializes
    // MilliDuration as a scalar long); the object form 500s. Verified against prod.
    filter.activatedWithin = CREATED_WITHIN_TO_MS[
      filters.createdWithin
    ] as unknown as HpWebListingFilterDto['activatedWithin'];
  const moveInDate = normalizeMoveInDate(filters.moveInDate.start, filters.moveInDate.end);
  if (moveInDate.end) filter.moveInDate = moveInDate.end;
  if (mustHave.length > 0) filter.mustHave = mustHave as Array<HpSearchFlag>;

  // Commute isochrone filter. commuteToV3 returns the structural wire shape —
  // maxDuration as a raw millisecond number (the generated { millis } object
  // form 500s — see activatedWithin) — so cast once at the DTO boundary.
  if (filters.commute)
    filter.commute = commuteToV3(filters.commute) as unknown as HotpadsWebListingFilter['commute'];

  return filter;
}
